// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// transparency is the client-side key-transparency WITNESS log. The server publishes a
// signed, hash-chained tree root (STR) committing the whole key directory; a single
// client can already prove its own key is included (the Profile audit). What a lone
// client can NOT detect is EQUIVOCATION — the server signing two different roots for the
// same epoch, showing Alice one directory and Bob another. Witnessing closes that gap:
// each device records every STR it observes (its own /root fetches + STRs gossiped by
// co-members), and flags a conflict —
//   • two validly-signed roots at the SAME epoch (a fork);
//   • a prev_hash that doesn't chain to the previously-witnessed epoch (history rewrite);
//   • the server's signing key changing under us.
// The server merely relays gossip; because every STR is server-SIGNED it can't forge a
// peer's view, only censor it (a documented limitation — censorship resistance, i.e.
// out-of-band STR exchange, is the follow-up). Gossiped roots are signature-checked via
// the Go engine before they're trusted as evidence, so a lying peer can't raise a false
// alarm.
import type { STR } from "./types";
import type { CryptoEngine } from "./crypto";

export type WitnessStatus = "new" | "ok" | "equivocation" | "chain_broken" | "server_key_changed" | "bad_signature";

export interface WitnessResult {
  status: WitnessStatus;
  epoch: number;
  detail?: string;
}

// A sticky alarm — once equivocation/tampering is observed it stays raised until the
// user dismisses it, since the evidence (two signed roots) doesn't go away.
export interface WitnessAlarm {
  status: "equivocation" | "chain_broken" | "server_key_changed";
  epoch: number;
  detail: string;
  observed: string; // peer user ID (or "self") that surfaced it
}

interface WitnessRecord {
  epoch: number;
  root: string;
  prevHash: string;
  hash: string; // canonical chain hash (from the engine), "" if unknown
  timestamp: number;
}

const logKey = (server: string) => `zalmoxis.witness.log.${server}`;
const pinKey = (server: string) => `zalmoxis.witness.serverkey.${server}`;
const alarmKey = (server: string) => `zalmoxis.witness.alarm.${server}`;

// Bump when a benign change in how the server numbers/chains STRs would otherwise
// make already-recorded witness state conflict with the new chain. Migrating drops
// the stale records once so the client re-baselines cleanly. v1: the server's STR
// epoch chain was in-memory, so a restart/redeploy (or a second replica) re-published
// a DIFFERENT root for an already-witnessed epoch — a benign reset the witness layer
// can't tell from a fork, leaving a sticky FALSE alarm. The server is now fixed
// (persistent, time-seeded chain); this clears the leftover false evidence.
const WITNESS_SCHEMA_VERSION = 1;
const schemaKey = "zalmoxis.witness.schema";

// migrateWitnessStore runs ONCE per client (version-gated) at startup. It drops the
// recorded roots + any raised alarm for every server, so the client re-baselines
// against the now-fixed chain — turning the one-time false alarm off without asking
// the user to do anything. The pinned server key is KEPT, so a genuine signing-key
// swap is still caught; and a REAL ongoing equivocation re-raises immediately on the
// next /root fetch or gossip (the evidence isn't gone, only the stale record is).
export function migrateWitnessStore(): void {
  let have = 0;
  try {
    have = Number(localStorage.getItem(schemaKey)) || 0;
  } catch {
    return; // storage unavailable (private mode) — nothing to migrate
  }
  if (have >= WITNESS_SCHEMA_VERSION) return;
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith("zalmoxis.witness.log.") || k.startsWith("zalmoxis.witness.alarm.")) localStorage.removeItem(k);
    }
    localStorage.setItem(schemaKey, String(WITNESS_SCHEMA_VERSION));
  } catch {
    /* best-effort */
  }
}

function readLog(server: string): Record<string, WitnessRecord> {
  try {
    return JSON.parse(localStorage.getItem(logKey(server)) || "{}");
  } catch {
    return {};
  }
}
function writeLog(server: string, log: Record<string, WitnessRecord>): void {
  // Keep the log bounded (the last ~200 epochs is plenty to chain-check + spot a fork).
  const epochs = Object.keys(log)
    .map(Number)
    .sort((a, b) => a - b);
  while (epochs.length > 200) delete log[String(epochs.shift())];
  localStorage.setItem(logKey(server), JSON.stringify(log));
}

export function pinnedServerKey(server: string): string | null {
  return localStorage.getItem(pinKey(server));
}

export function witnessAlarm(server: string): WitnessAlarm | null {
  try {
    const raw = localStorage.getItem(alarmKey(server));
    return raw ? (JSON.parse(raw) as WitnessAlarm) : null;
  } catch {
    return null;
  }
}
export function clearWitnessAlarm(server: string): void {
  localStorage.removeItem(alarmKey(server));
}
function raiseAlarm(server: string, a: WitnessAlarm): void {
  if (!witnessAlarm(server)) localStorage.setItem(alarmKey(server), JSON.stringify(a)); // keep the first evidence
}

// witnessedEpochs reports how many distinct epochs this device has recorded, and the
// latest — for a small status line in the Profile panel.
export function witnessedEpochs(server: string): { count: number; latest: number } {
  const log = readLog(server);
  const epochs = Object.keys(log).map(Number);
  return { count: epochs.length, latest: epochs.length ? Math.max(...epochs) : 0 };
}

// witnessSTR records an observed signed tree root and checks it for consistency against
// everything seen before. `source` is "self" (my own /root fetch, already trusted) or a
// peer's user ID (gossip — signature-verified here before it's believed).
export async function witnessSTR(engine: CryptoEngine, server: string, str: STR, serverKey: string, source: string): Promise<WitnessResult> {
  const epoch = Number(str.epoch);
  // Verify the signature (and get the canonical chain hash) through the Go engine — the
  // SAME canonicalisation the server signed with, so there's no JS reimplementation to
  // drift. A peer with a bad signature is simply ignored (no alarm — they could be lying).
  let hash = "";
  try {
    const v = await engine.verifySTRSignature(JSON.stringify({ str, server_key: serverKey }));
    if (!v.verified) return { status: "bad_signature", epoch, detail: v.error };
    hash = v.hash || "";
  } catch (e) {
    return { status: "bad_signature", epoch, detail: (e as Error).message };
  }

  // Server signing-key continuity (a swap is itself an attack the user should see).
  const pinned = pinnedServerKey(server);
  if (!pinned) {
    localStorage.setItem(pinKey(server), serverKey);
  } else if (pinned !== serverKey) {
    const res: WitnessResult = { status: "server_key_changed", epoch, detail: "the homeserver's signing key changed" };
    raiseAlarm(server, { status: "server_key_changed", epoch, detail: res.detail!, observed: source });
    return res;
  }

  const log = readLog(server);

  // Same epoch, different root → the server signed two conflicting directories. Caught.
  const existing = log[String(epoch)];
  if (existing && existing.root !== str.root) {
    const detail = `the homeserver published two different roots for epoch ${epoch}`;
    raiseAlarm(server, { status: "equivocation", epoch, detail, observed: source });
    return { status: "equivocation", epoch, detail };
  }

  // Chain linkage: this epoch's prev_hash must match the hash of the epoch before it.
  const prev = log[String(epoch - 1)];
  if (prev && prev.hash && str.prev_hash && prev.hash !== str.prev_hash) {
    const detail = `epoch ${epoch} does not chain to the witnessed epoch ${epoch - 1} (history rewrite)`;
    raiseAlarm(server, { status: "chain_broken", epoch, detail, observed: source });
    return { status: "chain_broken", epoch, detail };
  }

  log[String(epoch)] = { epoch, root: str.root, prevHash: str.prev_hash, hash, timestamp: Number(str.timestamp) };
  writeLog(server, log);
  return { status: existing ? "ok" : "new", epoch };
}
